import { api } from "./api";

/**
 * Registra el service worker de push y devuelve el registration.
 * Se llama desde `App.js` / `AuthContext` al cargar, para que el SW
 * esté listo cuando el cliente decida activar notificaciones.
 */
export async function registerPushServiceWorker() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js");
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("SW register failed:", e);
    return null;
  }
}

/**
 * Convierte una clave VAPID base64-url (como la que devuelve
 * `/api/push/vapid`) al Uint8Array que espera `PushManager.subscribe`.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Estado actual de permisos/suscripción — para el toggle del perfil. */
export async function getPushStatus() {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return { supported: false, permission: "default", subscribed: false };
  }
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
  };
}

/**
 * Pide permiso al navegador (si hace falta), se suscribe al push
 * manager con la VAPID pública del backend, y guarda la subscripción
 * en MongoDB via `/api/push/subscribe`. Retorna `true` si terminó
 * suscrito; `false` si el usuario rechazó o el navegador no soporta.
 */
export async function enablePush() {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" };
  }
  // 1) Permisos
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") {
    return { ok: false, reason: "denied" };
  }
  // 2) Asegurar SW registrado
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await registerPushServiceWorker();
  if (!reg) return { ok: false, reason: "sw-failed" };
  // 3) Obtener VAPID pública del backend
  const { data } = await api.get("/push/vapid");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);
  // 4) Suscribir
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
  // 5) Guardar en backend (associate with authenticated user)
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    user_agent: navigator.userAgent,
  });
  return { ok: true };
}

export async function disablePush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  const raw = sub.toJSON();
  try {
    await api.delete("/push/unsubscribe", { data: { endpoint: raw.endpoint } });
  } catch (_e) { /* best-effort */ }
  try { await sub.unsubscribe(); } catch (_e) { /* ignore */ }
}

export async function sendTestPush() {
  try {
    await api.post("/push/test", {});
    return true;
  } catch {
    return false;
  }
}
